import React, { useEffect, useState, useMemo } from 'react';
import { Table, Radio, Form } from 'antd';
import { LeftOutlined, RightOutlined, EditOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { getStationSummary, getFinanceByStationMonth } from '../../modules/analytics';
import './financeTable.scss';
import FinanceEditModal from './FinanceEditModal';

const FinanceTable = ({ stationId }) => {
  const dispatch = useDispatch();
  const inspectionTime = useSelector(state => state.analytics.inspectionTime);
  const summary = useSelector(
    state => (state.analytics.stationSummary || {})[stationId] || {}
  );
  const financeByMonth = useSelector(
    state => ((state.analytics.financeByMonth || {})[stationId]) || {}
  );
  const [view, setView] = useState('monthly');

  // Guard set to avoid duplicate finance fetches for months
  const requestedMonthsRef = React.useRef(new Set());

  // Extract available years strictly from summary.monthlyTotals
  const availableYears = useMemo(() => {
    const srcMonths = (summary.monthlyTotals || []).map(m => m.month);
    const yrs = srcMonths.map(mon => mon.slice(0,4));
    return Array.from(new Set(yrs)).sort();
  }, [summary.monthlyTotals]);

  const [year, setYear] = useState(
    availableYears.length ? availableYears[availableYears.length - 1] : new Date().getFullYear()
  );

  // Build monthly data rows: total row + months sorted descending for the selected year
  const monthlyData = useMemo(() => {
    // Months come strictly from summary.monthlyTotals
    const monthsInYear = (summary.monthlyTotals || [])
      .map(m => m.month)
      .filter(mon => mon.slice(0,4) === String(year));
    const monthlyList = monthsInYear.sort((a,b) => b.localeCompare(a));

    // Build detailed rows for each month using finance records
    const rows = monthlyList.map(mon => {
      const fin = financeByMonth[mon] || {};
      const grid = fin.grid || {};
      const site = fin.site || {};
      const summaryFin = fin.summary || {};
      const siteIsFixed = (site.priceModel || site.model || '').toUpperCase() === 'FIXED';
      const siteFixedPrice = site.fixedPrice != null ? Number(site.fixedPrice) : null;
      const siteKwh = site.kwh != null ? Number(site.kwh) : null;
      const gridKwh = grid.kwh != null ? Number(grid.kwh) : null;
      const gridFit = grid.fit != null ? Number(grid.fit) : null;
      const discountRatio = fin.discountKwh != null
        ? Math.max(0, Math.min(Number(fin.discountKwh), 1))
        : null;

      const blops = Array.isArray(site.blops) ? site.blops : [];
      const blopsAmount = blops.reduce((acc, b) => acc + (Number(b.unitPrice || 0) * Number(b.usage || 0)), 0);
      const siteAvgPrice = (blops.length && siteKwh > 0) ? (blopsAmount / siteKwh) : null;
      const calcSiteAmount = siteIsFixed && siteKwh != null && siteFixedPrice != null
        ? siteKwh * siteFixedPrice
        : (blops.length ? (blopsAmount * (discountRatio != null ? discountRatio : 1)) : (fin.siteAmount != null ? Number(fin.siteAmount) : null));

      const calcGridAmount = gridKwh != null && gridFit != null
        ? gridKwh * gridFit
        : (fin.gridAmount != null ? Number(fin.gridAmount) : null);

      const totalKwh = (siteKwh || 0) + (gridKwh || 0);
      const totalAmount = (calcSiteAmount || 0) + (calcGridAmount || 0);
      const totalUnitPrice = totalKwh > 0 ? (totalAmount / totalKwh) : null;
      const selfUtilization = summaryFin.selfUtilization ?? null;
      const capacityKwRow = Number(
        inspectionTime?.stationInfo?.capacityKw ||
        inspectionTime?.stationInfo?.capacity ||
        0
      );
      const utilizationHours = capacityKwRow > 0 ? (totalKwh / capacityKwRow) : null;

      let priceModelPrice = null;
      let priceModelName = null;
      if (siteIsFixed && siteFixedPrice != null) {
        priceModelPrice = siteFixedPrice;
        priceModelName = 'Fixed Solar Price';
      } else if (!siteIsFixed && blops.length > 0 && discountRatio != null) {
        priceModelPrice = discountRatio;
        priceModelName = 'BLOPS Discount Ratio';
      }

      return {
        key: `m-${mon}`,
        period: mon,
        energy: totalKwh,
        revenue: totalAmount,
        totalKwh,
        totalUnitPrice,
        totalAmount,
        siteKwh: siteKwh ?? null,
        siteUnitPrice: siteIsFixed ? siteFixedPrice : (siteAvgPrice ?? null),
        siteAmount: calcSiteAmount ?? null,
        gridKwh: gridKwh ?? null,
        gridUnitPrice: gridFit ?? null,
        gridAmount: calcGridAmount ?? null,
        priceModelPrice,
        priceModelName,
        selfUtilization,
        utilizationHours,
      };
    });

    // Aggregate totals from the detailed monthly rows
    const totalRowAgg = rows.reduce((acc, r) => {
      acc.totalKwh += r.totalKwh || 0;
      acc.totalAmount += r.totalAmount || 0;
      acc.siteKwh += r.siteKwh || 0;
      acc.siteAmount += r.siteAmount || 0;
      acc.gridKwh += r.gridKwh || 0;
      acc.gridAmount += r.gridAmount || 0;
      if (r.selfUtilization != null) {
        acc.wSelf += Number(r.selfUtilization) * (r.totalKwh || 0);
        acc.wDen += r.totalKwh || 0;
      }
      if (r.utilizationHours != null) {
        acc.wUtil += Number(r.utilizationHours) * (r.totalKwh || 0);
      }
      return acc;
    }, { totalKwh:0,totalAmount:0,siteKwh:0,siteAmount:0,gridKwh:0,gridAmount:0,wSelf:0,wUtil:0,wDen:0 });

    const capacityKw = Number(
      inspectionTime?.stationInfo?.capacityKw ||
      inspectionTime?.stationInfo?.capacity ||
      0
    );
    const yearSelfUtil = totalRowAgg.totalKwh > 0
      ? (totalRowAgg.siteKwh / totalRowAgg.totalKwh) * 100
      : null;
    const yearUtilHours = capacityKw > 0
      ? (totalRowAgg.totalKwh / capacityKw)
      : null;

    const totalRow = {
      key: `year-${year}-total`,
      period: `${year} (Total)`,
      __isTotal: true,
      totalKwh: totalRowAgg.totalKwh,
      totalUnitPrice: (totalRowAgg.totalKwh>0 ? (totalRowAgg.totalAmount/totalRowAgg.totalKwh) : null),
      totalAmount: totalRowAgg.totalAmount,
      siteKwh: totalRowAgg.siteKwh || null,
      siteUnitPrice: totalRowAgg.siteKwh>0 ? totalRowAgg.siteAmount/totalRowAgg.siteKwh : null,
      siteAmount: totalRowAgg.siteAmount || null,
      gridKwh: totalRowAgg.gridKwh || null,
      gridUnitPrice: totalRowAgg.gridKwh>0 ? totalRowAgg.gridAmount/totalRowAgg.gridKwh : null,
      gridAmount: totalRowAgg.gridAmount || null,
      priceModelPrice: null,
      priceModelName: null,
      selfUtilization: yearSelfUtil != null ? yearSelfUtil.toFixed(2) : null,
      utilizationHours: yearUtilHours != null ? yearUtilHours.toFixed(2) : null,
    };

    return [totalRow, ...rows];
  }, [summary.monthlyTotals, inspectionTime, year, financeByMonth]);

  // Build annual data rows by aggregating monthly totals, using only finance records
  const annualData = useMemo(() => {
    // Build per-month rows enriched from finance map only
    const rows = Object.entries(financeByMonth || {}).map(([month, fin]) => {
      const grid = fin.grid || {};
      const site = fin.site || {};
      const sum  = fin.summary || {};
      const siteIsFixed = (site.priceModel || site.model || '').toUpperCase() === 'FIXED';
      const siteFixedPrice = site.fixedPrice != null ? Number(site.fixedPrice) : null;
      const siteKwh = site.kwh != null ? Number(site.kwh) : 0;
      const gridKwh = grid.kwh != null ? Number(grid.kwh) : 0;
      const gridFit = grid.fit != null ? Number(grid.fit) : null;
      const discountRatio = fin.discountKwh != null ? Math.max(0, Math.min(Number(fin.discountKwh), 1)) : null;
      const blops = Array.isArray(site.blops) ? site.blops : [];
      const blopsAmount = blops.reduce((acc, b) => acc + (Number(b.unitPrice || 0) * Number(b.usage || 0)), 0);
      const siteAmount = siteIsFixed && siteKwh && siteFixedPrice != null
        ? siteKwh * siteFixedPrice
        : (blops.length ? blopsAmount * (discountRatio != null ? discountRatio : 1) : 0);
      const gridAmount = (gridKwh && gridFit != null) ? gridKwh * gridFit : 0;
      const totalKwh = siteKwh + gridKwh;
      return {
        year: month.slice(0, 4),
        totalKwh,
        totalAmount: siteAmount + gridAmount,
        siteKwh,
        siteAmount,
        gridKwh,
        gridAmount,
        isBlops: !siteIsFixed && blops.length > 0,
        isFixed: !!siteIsFixed,
        selfUtilization: sum.selfUtilization != null ? Number(sum.selfUtilization) : null,
        utilizationHours: sum.utilizationHours != null ? Number(sum.utilizationHours) : null,
      };
    });

    // Aggregate by year
    const agg = rows.reduce((acc, r) => {
      const y = r.year;
      if (!acc[y]) acc[y] = {
        key: `y-${y}`,
        period: y,
        totalKwh: 0,
        totalAmount: 0,
        siteKwh: 0,
        siteAmount: 0,
        gridKwh: 0,
        gridAmount: 0,
        anyBlops: false,
        anyFixed: false,
        // weighted averages
        wSelfUtilSum: 0,
        wUtilHourSum: 0,
        wDen: 0,
      };

      acc[y].totalKwh += r.totalKwh;
      acc[y].totalAmount += r.totalAmount;
      acc[y].siteKwh += r.siteKwh;
      acc[y].siteAmount += r.siteAmount;
      acc[y].gridKwh += r.gridKwh;
      acc[y].gridAmount += r.gridAmount;
      acc[y].anyBlops = acc[y].anyBlops || r.isBlops;
      acc[y].anyFixed = acc[y].anyFixed || r.isFixed;

      const w = r.totalKwh || 0; // weight by total energy that month
      if (r.selfUtilization != null) acc[y].wSelfUtilSum += r.selfUtilization * w;
      if (r.utilizationHours != null) acc[y].wUtilHourSum += r.utilizationHours * w;
      acc[y].wDen += w;

      return acc;
    }, {});

    // Finalize rows
    return Object.values(agg).map(row => {
      const { siteAmount, siteKwh, gridAmount, gridKwh, anyBlops, anyFixed, wSelfUtilSum, wUtilHourSum, wDen } = row;
      const siteAvgPrice = siteKwh > 0 ? (siteAmount / siteKwh) : null;
      const gridAvgPrice = gridKwh > 0 ? (gridAmount / gridKwh) : null;
      const weightedSelfUtil = wDen > 0 ? (wSelfUtilSum / wDen) : null;
      const weightedUtilHours = wDen > 0 ? (wUtilHourSum / wDen) : null;
      return {
        ...row,
        totalUnitPrice: row.totalKwh>0 ? (row.totalAmount/row.totalKwh) : null,
        siteUnitPrice: siteAvgPrice,
        gridUnitPrice: gridAvgPrice,
        priceModelPrice: null,
        priceModelName: null,
        selfUtilization: weightedSelfUtil != null ? Number(weightedSelfUtil.toFixed(2)) : null,
        utilizationHours: weightedUtilHours != null ? Number(weightedUtilHours.toFixed(2)) : null,
      };
    });
  }, [summary.monthlyTotals, financeByMonth, inspectionTime]);

  const dataSource = view === 'monthly' ? monthlyData : annualData;

  const formatEnergy = n => {
    const num = Math.round(Number(n) || 0);
    return `${num.toLocaleString()} kWh`;
  };
  const formatCurrency = n =>
    `¥ ${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  const renderMoneyEnergy = (amount, kwh, unitPrice) => {
    // Normalize numbers
    const hasUnit = unitPrice != null;
    const unit = hasUnit ? Number(unitPrice) : null;
    const explicitAmt = amount != null ? Number(amount) : null;
    const impliedAmt = (kwh != null && unit != null) ? (Number(kwh) * unit) : null;
    const amt = explicitAmt != null ? explicitAmt : impliedAmt;

    // If cell-main would be "¥ 0" or unit price is 0, show "-"
    if ((amt != null && amt <= 0) || (unit != null && unit <= 0)) {
      return <span>-</span>;
    }

    if (amt == null && (kwh == null || unitPrice == null)) return <span>-</span>;

    const kwhText = kwh != null ? `${Math.round(Number(kwh)).toLocaleString()}kWh` : '-';
    const unitText = unit != null ? `¥${unit.toLocaleString(undefined, { maximumFractionDigits: 2 })}/kWh` : '-';

    return (
      <span className="cell-stack">
        <span className="cell-main">{`¥ ${Number(amt).toLocaleString(undefined, { maximumFractionDigits: 0 })}`}</span>
        <span className="cell-sub">{kwhText} &nbsp; ({unitText})</span>
      </span>
    );
  };
  const renderPriceModel = (price, name) => {
    if ((price != null && Number(price) <= 0) || (price == null && !name)) return <span>-</span>;
    const isRatio = (name || '').toLowerCase().includes('blops');
    const mainText = price != null
      ? (isRatio
          ? `${Number(price).toLocaleString(undefined, { maximumFractionDigits: 2 })}`
          : `¥${Number(price).toLocaleString(undefined, { maximumFractionDigits: 2 })}/kWh`)
      : '-';
    const nameText = name || (isRatio ? 'BLOPS Discount Ratio' : 'Fixed Solar Price');
    return (
      <span className="cell-stack">
        <span className="cell-main">{mainText}</span>
        <span className="cell-sub">{nameText}</span>
      </span>
    );
  };
  const renderPercent = (v) => (v == null ? '-' : <span className="cell-strong">{`${v}%`}</span>);
  const renderNumber = (v) => (v == null ? '-' : <span className="cell-strong">{Number(v).toLocaleString()}</span>);

  const baseColumns = [
    { title: 'Time', dataIndex: 'period', key: 'period' },
    { title: <span className="th-multiline">Total Energy<br/>Sold</span>, key: 'total', render: (_, r) => renderMoneyEnergy(r.totalAmount, r.totalKwh, r.totalUnitPrice) },
    { title: <span className="th-multiline">Energy sold<br/>to Site</span>, key: 'toSite', render: (_, r) => renderMoneyEnergy(r.siteAmount, r.siteKwh, r.siteUnitPrice) },
    { title: <span className="th-multiline">Energy Sold<br/>to Grid</span>, key: 'toGrid', render: (_, r) => renderMoneyEnergy(r.gridAmount, r.gridKwh, r.gridUnitPrice) },
    { title: <span className="th-multiline">Solar Price<br/>Model</span>, key: 'priceModel', render: (_, r) => renderPriceModel(r.priceModelPrice, r.priceModelName) },
    { title: <span className="th-multiline">Self<br/>Utilization</span>, dataIndex: 'selfUtilization', key: 'utilization', render: renderPercent },
    { title: <span className="th-multiline">Utilization<br/>Hours</span>, dataIndex: 'utilizationHours', key: 'hours', render: renderNumber },
  ];
  const columns = useMemo(() => {
    if (view !== 'monthly') return baseColumns;
    return [
      ...baseColumns,
      {
        title: '',
        key: 'edit',
        dataIndex: 'edit',
        className: 'column-edit-icon',
        align: 'center',
        render: (_, record) => (
          record.__isTotal ? null : (
            <EditOutlined
              className="editIcon"
              title="Edit"
              onClick={(e) => {
                e.stopPropagation();
                openEdit(record);
              }}
            />
          )
        )
      }
    ];
  }, [view]);

  useEffect(() => {
    if (stationId) dispatch(getStationSummary(stationId));
  }, [dispatch, stationId]);

  // Fetch only missing months for the selected year; guard against re-dispatch/races
  useEffect(() => {
    if (!stationId) return;
    const yearStr = String(year);
    const monthsInYear = (summary.monthlyTotals || [])
      .map(m => m.month)
      .filter(mon => mon.slice(0, 4) === yearStr);

    monthsInYear.forEach(mon => {
      if (financeByMonth[mon]) return; // already in store
      if (requestedMonthsRef.current.has(mon)) return; // already requested
      requestedMonthsRef.current.add(mon);
      dispatch(getFinanceByStationMonth(stationId, mon));
    });
  }, [dispatch, stationId, year, summary.monthlyTotals, financeByMonth]);

  // Reset request guard when station changes
  useEffect(() => {
    requestedMonthsRef.current.clear();
  }, [stationId]);

  // =========================
  // Edit modal for monthly rows
  // =========================
  const [isEditOpen, setEditOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [form] = Form.useForm();

  const openEdit = (row) => {
    setEditingRow(row);
    // seed form values from the row if available
    form.setFieldsValue({
      reportMonth: row.period,
      generatedKwh: Math.round(row.totalKwh || 0),
      gridKwh: row.gridKwh != null ? Math.round(row.gridKwh) : undefined,
      gridFit: row.gridUnitPrice,
      siteKwh: row.siteKwh != null ? Math.round(row.siteKwh) : undefined,
      sitePrice: row.siteUnitPrice,
      // BLOPS quick inputs
      blops: [
        { tier: 'Super Peak', unitPrice: undefined, usage: undefined },
        { tier: 'Peak', unitPrice: undefined, usage: undefined },
        { tier: 'Off Peak', unitPrice: undefined, usage: undefined },
        { tier: 'Bottom', unitPrice: undefined, usage: undefined },
      ],
    });
    setEditOpen(true);
  };

  const closeEdit = () => setEditOpen(false);

  const onSubmitEdit = () => {
    form
      .validateFields()
      .then((vals) => {
        // For now we just close; integration with backend can be added later
        setEditOpen(false);
      })
      .catch(() => {});
  };

  return (
    <div className="financeTableContainer">
      <div className="tableHeaderFlex">
        <Radio.Group className="tableControls" value={view} onChange={e => setView(e.target.value)}>
          <Radio.Button value="monthly">
            Monthly Summary
          </Radio.Button>
          <Radio.Button value="annual">
            Annual Summary
          </Radio.Button>
        </Radio.Group>
        <div className="yearNavigator">
          <LeftOutlined
            onClick={() => {
              const idx = availableYears.indexOf(year.toString());
              if (idx > 0) setYear(availableYears[idx - 1]);
            }}
            disabled={availableYears.indexOf(year.toString()) <= 0}
            className="yearNav"
          />
          <span className="yearDisplay">{year}</span>
          <RightOutlined
            onClick={() => {
              const idx = availableYears.indexOf(year.toString());
              if (idx < availableYears.length - 1) setYear(availableYears[idx + 1]);
            }}
            disabled={
              availableYears.indexOf(year.toString()) === -1 ||
              availableYears.indexOf(year.toString()) >= availableYears.length - 1
            }
            className="yearNav"
          />
        </div>
      </div>
      <Table
        components={{
          header: {
            row: (rowProps) => <tr {...rowProps} className="tableHeaderRow" />,
          },
        }}
        className={`financeTable ${view === 'monthly' ? 'monthlyView' : ''}`}
        columns={columns}
        dataSource={dataSource}
        pagination={false}
        rowKey="key"
      />
      <FinanceEditModal
        isOpen={isEditOpen}
        editingRow={editingRow}
        inspectionTime={inspectionTime}
        form={form}
        onSubmit={onSubmitEdit}
        onClose={closeEdit}
      />
    </div>
  );
};

export default FinanceTable;
